import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { Link, Stack, Switch, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { dataGridIdSlots } from 'v2/components/DataGrid/dataGridIdSlots';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import DetailDataGridContainer from 'v2/components/DetailDataGridContainer/DetailDataGridContainer';
import SearchAndFilters, { FilterChip, FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import { TextCell } from 'v2/components/TextCell/TextCell';
import { DATAGRID_DEFAULT_PAGE_SIZE, DATAGRID_PAGE_SIZE_OPTIONS, DATAGRID_ROW_HEIGHT } from 'v2/constants';
import { FilterOfficeMode } from 'v2/filters/userGroups/userGroupFilterTypes';
import { UserGroupRow } from 'v2/hooks/useDeviceGroupDetails/useDeviceGroupDetails';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import { getUserGroupPath } from 'v2/paths';
import { getOfficeModeDisplay } from 'v2/utils/scheduleDisplay';

const searchKeys = ['name'];

interface Props {
  userGroups: UserGroupRow[];
  onToggle: (userGroup: UserGroupRow, enabled: boolean) => Promise<void>;
}

interface UserGroupFilterAccess {
  noUsers: boolean;
  noSchedule: boolean;
}

const accessFilterDefault: UserGroupFilterAccess = { noUsers: false, noSchedule: false };

const UserGroupsTab: React.FC<Props> = ({ userGroups, onToggle }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filterStr, setFilterStr] = useState('');
  const [isToggling, setIsToggling] = useState(false);
  const [accessFilter, setAccessFilter] = useState<UserGroupFilterAccess>(accessFilterDefault);
  const [officeModeFilter, setOfficeModeFilter] = useState<FilterOfficeMode>(null);

  const anyAccessEnabled = accessFilter.noUsers || accessFilter.noSchedule;
  const isAnyFilterEnabled = anyAccessEnabled || officeModeFilter !== null;

  let filteredGroups = useSearch({ data: userGroups, search: filterStr, keys: searchKeys });

  if (anyAccessEnabled) {
    filteredGroups = filteredGroups.filter(
      (group) =>
        (!accessFilter.noUsers || group.members.length === 0) &&
        (!accessFilter.noSchedule || getOfficeModeDisplay(group.accessGrant?.accessSchedule) === '-')
    );
  }

  if (officeModeFilter === FilterOfficeMode.Allowed) {
    filteredGroups = filteredGroups.filter((group) => group.accessGrant?.isOfficeModeEnabled === true);
  } else if (officeModeFilter === FilterOfficeMode.NotAllowed) {
    filteredGroups = filteredGroups.filter((group) => !group.accessGrant?.isOfficeModeEnabled);
  }

  const toggleAccess = (key: keyof UserGroupFilterAccess) => {
    setAccessFilter((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const sections: FilterSection[] = [
    {
      id: 'access',
      header: translation.access,
      type: 'checkbox',
      isClearDisabled: !anyAccessEnabled,
      onClear: () => setAccessFilter(accessFilterDefault),
      items: [
        {
          key: 'noUsers',
          label: translation.no_users,
          checked: accessFilter.noUsers,
          onChange: () => toggleAccess('noUsers')
        },
        {
          key: 'noSchedule',
          label: translation.no_schedule,
          checked: accessFilter.noSchedule,
          onChange: () => toggleAccess('noSchedule')
        }
      ]
    },
    {
      id: 'office_mode',
      header: translation.office_mode,
      type: 'radio',
      isClearDisabled: officeModeFilter === null,
      onClear: () => setOfficeModeFilter(null),
      items: [
        {
          key: FilterOfficeMode.Allowed,
          label: translation.allowed,
          checked: officeModeFilter === FilterOfficeMode.Allowed,
          onChange: () => setOfficeModeFilter(FilterOfficeMode.Allowed)
        },
        {
          key: FilterOfficeMode.NotAllowed,
          label: translation.not_allowed,
          checked: officeModeFilter === FilterOfficeMode.NotAllowed,
          onChange: () => setOfficeModeFilter(FilterOfficeMode.NotAllowed)
        }
      ]
    }
  ];

  const chips: FilterChip[] = [
    ...(accessFilter.noUsers ? [{ label: translation.no_users, onDelete: () => toggleAccess('noUsers') }] : []),
    ...(accessFilter.noSchedule
      ? [{ label: translation.no_schedule, onDelete: () => toggleAccess('noSchedule') }]
      : []),
    ...(officeModeFilter === FilterOfficeMode.Allowed
      ? [{ label: translation.allowed, onDelete: () => setOfficeModeFilter(null) }]
      : []),
    ...(officeModeFilter === FilterOfficeMode.NotAllowed
      ? [{ label: translation.not_allowed, onDelete: () => setOfficeModeFilter(null) }]
      : [])
  ];

  const headerWithId = renderHeaderWithId();

  const columns: GridColDef<UserGroupRow>[] = [
    {
      field: 'name',
      headerName: translation.name,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      renderCell: (params) => (
        <Link
          id={`user-group-${params.row.id}-link`}
          component={RouterLink}
          to={getUserGroupPath(params.row.id)}
          underline="none"
        >
          <TextCell>{params.value}</TextCell>
        </Link>
      )
    },
    {
      field: 'members',
      headerName: translation.users,
      width: 100,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => row.members.length,
      renderCell: (params) =>
        params.row.members.length === 0 ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <WarningAmberOutlinedIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
            <span>{translation.no_users}</span>
          </Stack>
        ) : (
          params.row.members.length
        )
    },
    {
      field: 'schedule',
      headerName: translation.schedule,
      flex: 1,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => getOfficeModeDisplay(row.accessGrant?.accessSchedule),
      renderCell: (params) => {
        const display = getOfficeModeDisplay(params.row.accessGrant?.accessSchedule);
        return display === '-' ? (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <WarningAmberOutlinedIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />
            <span>{translation.no_schedule}</span>
          </Stack>
        ) : (
          display
        );
      }
    },
    {
      field: 'isOfficeModeEnabled',
      headerName: translation.office_mode,
      width: 120,
      sortable: true,
      renderHeader: headerWithId,
      valueGetter: (_, row) => (row.accessGrant?.isOfficeModeEnabled ? translation.yes : translation.no)
    },
    {
      field: 'isEnabled',
      headerName: '',
      width: 80,
      sortable: false,
      renderHeader: headerWithId,
      renderCell: (params) => (
        <Switch
          id={`user-group-${params.row.id}-enabled-switch`}
          checked={params.row.isEnabled}
          disabled={isToggling}
          onChange={async (event) => {
            event.stopPropagation();
            setIsToggling(true);
            await onToggle(params.row, event.target.checked);
            setIsToggling(false);
          }}
        />
      )
    }
  ];

  const mobileList = (
    <Stack spacing={1}>
      {filteredGroups.map((group) => (
        <Stack
          key={group.id}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ backgroundColor: theme.custom.background.level3, px: 2, py: 1 }}
        >
          <Link
            id={`user-group-${group.id}-link`}
            component={RouterLink}
            to={getUserGroupPath(group.id)}
            underline="none"
            color="inherit"
          >
            <Typography variant="body2">{group.name}</Typography>
          </Link>
          <Switch
            id={`user-group-${group.id}-enabled-switch`}
            checked={group.isEnabled}
            disabled={isToggling}
            onChange={async (event) => {
              event.stopPropagation(); // just to be sure
              setIsToggling(true);
              await onToggle(group, !group.isEnabled);
              setIsToggling(false);
            }}
          />
        </Stack>
      ))}
    </Stack>
  );

  return (
    <Stack spacing={2}>
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={sections}
        chips={chips}
        countCaption={`${filteredGroups.length} ${translation.of} ${userGroups.length} ${translation.user_groups}`}
      />
      {isMobile ? (
        mobileList
      ) : (
        <DetailDataGridContainer>
          <DataGrid
            className="background-level2 not-clickable"
            rows={filteredGroups}
            columns={columns}
            autoHeight
            hideFooter={filteredGroups.length <= DATAGRID_DEFAULT_PAGE_SIZE}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            initialState={{ pagination: { paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE } } }}
            rowHeight={DATAGRID_ROW_HEIGHT}
            disableRowSelectionOnClick
            disableColumnMenu
            slots={dataGridIdSlots}
          />
        </DetailDataGridContainer>
      )}
    </Stack>
  );
};

export default UserGroupsTab;
